import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { AssetListItem } from "../api/types";
import { VideoCard } from "../components/VideoCard";
import { SearchBar } from "../components/SearchBar";
import { useAuth, canUpload } from "../auth/AuthContext";
import { useT } from "../i18n";

export function HomePage() {
  const { user } = useAuth();
  const { t, locale } = useT();
  const [items, setItems] = useState<AssetListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);

  const fetchVideos = useCallback(async (q: string, tag: string | null, loc: string) => {
    setLoading(true);
    try {
      const res = await api.listAssets(50, undefined, q || undefined, {
        status: "READY",
        tag: tag ?? undefined,
      }, loc);
      setItems(res.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos(query, activeTag, locale);
  }, [query, activeTag, locale, fetchVideos]);

  function handleSearch(q: string) {
    setActiveTag(null);
    setQuery(q);
  }

  function handleTagSelect(tag: string) {
    setQuery("");
    setActiveTag(tag);
  }

  function handleClearTag() {
    setActiveTag(null);
  }

  async function handleDelete(id: string, _title: string) {
    try {
      await api.deleteAsset(id);
      setItems((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="px-4 py-4">
      <SearchBar
        value={query}
        onSearch={handleSearch}
        onTagSelect={handleTagSelect}
        placeholder={t("home.search")}
      />

      {activeTag && (
        <div className="flex justify-center mt-3">
          <button
            onClick={handleClearTag}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-hover border border-brand-line text-sm hover:bg-brand-line transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="text-brand-muted">
              <path d="M5.5,7A1.5,1.5 0 0,1 4,5.5A1.5,1.5 0 0,1 5.5,4A1.5,1.5 0 0,1 7,5.5A1.5,1.5 0 0,1 5.5,7M21.41,11.58L12.41,2.58C12.05,2.22 11.55,2 11,2H4C2.89,2 2,2.89 2,4V11C2,11.55 2.22,12.05 2.59,12.41L11.58,21.41C11.95,21.77 12.45,22 13,22C13.55,22 14.05,21.77 14.41,21.41L21.41,14.41C21.78,14.05 22,13.55 22,13C22,12.44 21.77,11.94 21.41,11.58Z" />
            </svg>
            <span className="text-brand-text">{activeTag}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}

      {error && (
        <div className="text-red-400 mb-4">{t("home.loadFailed", { error })}</div>
      )}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-video rounded-xl bg-brand-surface" />
              <div className="h-4 mt-3 w-3/4 rounded bg-brand-surface" />
              <div className="h-3 mt-2 w-1/2 rounded bg-brand-surface" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-brand-muted py-20">
          <p className="text-lg">{query || activeTag ? t("home.noVideosMatch") : t("home.noVideosYet")}</p>
          {!query && !activeTag && <p className="text-sm mt-1">{t("home.uploadToStart")}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 mt-6">
          {items.map((a) => (
            <VideoCard
              key={a.id}
              asset={a}
              onDelete={canUpload(user?.role) ? handleDelete : undefined}
              onTagClick={handleTagSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
